import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowUpRight, Sparkles } from 'lucide-react';
import { contactInfo, budgetRanges } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { trackEvent } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', company: '', budget: '', brief: '' });
  const [sending, setSending] = useState(false);
  const [previewContext, setPreviewContext] = useState(null);

  // Pull the last brand-preview run and pre-fill the brief so leads land warm.
  useEffect(() => {
    try {
      const raw = localStorage.getItem('smg_brand_preview_lead');
      if (!raw) return;
      const data = JSON.parse(raw);
      // Discard if older than 2 hours
      if (!data || !data.ts || Date.now() - data.ts > 1000 * 60 * 60 * 2) {
        localStorage.removeItem('smg_brand_preview_lead');
        return;
      }
      setPreviewContext(data);
      setForm((f) => ({
        ...f,
        company: f.company || data.brand || '',
        brief:
          f.brief ||
          `Ran the SMG preview for ${data.brand}.\n\nThe thesis you generated:\n"${data.thesis}"\n\nTagline: ${data.tagline}\n\nInterested in what building this together would look like.`,
      }));
    } catch { /* noop */ }
  }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.brief) {
      toast({ title: 'Missing fields', description: 'Please complete name, email and brief.' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact/main`, form, { timeout: 25000 });
      trackEvent('generate_lead', {
        event_category: 'contact',
        event_label: form.budget || 'unspecified',
        has_brand_preview: !!previewContext,
      });
      try {
        const saved = JSON.parse(localStorage.getItem('smg_inquiries') || '[]');
        saved.push({ ...form, at: new Date().toISOString() });
        localStorage.setItem('smg_inquiries', JSON.stringify(saved));
        localStorage.removeItem('smg_brand_preview_lead');
      } catch {}
      toast({ title: 'Transmission sent', description: "We'll be in touch within 48h." });
      setForm({ name: '', email: '', company: '', budget: '', brief: '' });
      setPreviewContext(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'please try again in a moment';
      toast({ title: 'Could not send', description: detail });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative px-5 md:px-10 lg:px-14 py-24 md:py-32 border-t border-white/5">
      <div className="flex items-center gap-4 mb-10">
        <span className="w-10 h-px bg-[#FF3D2E]" />
        <p className="text-[10px] md:text-[11px] tracking-[0.3em] text-white/70">06 / CONTACT</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left info */}
        <div className="lg:col-span-5">
          <h2 className="font-display text-[10vw] md:text-[6vw] lg:text-[5.5rem] leading-[0.95] tracking-tight text-white">
            Tell us<br />what you're<br />
            <span className="font-script text-[#FF3D2E]">building.</span>
          </h2>

          <div className="mt-12 space-y-10">
            <div>
              <p className="text-[10px] tracking-[0.3em] text-white/45 mb-3">EMAIL</p>
              <a href={`mailto:${contactInfo.email}`} className="text-white text-lg md:text-xl hover:text-[#FF3D2E] transition-colors">
                {contactInfo.email}
              </a>
            </div>

            <div>
              <p className="text-[10px] tracking-[0.3em] text-white/45 mb-4">STUDIOS</p>
              <div className="space-y-4">
                {contactInfo.studios.map((s) => (
                  <div key={s.city}>
                    <p className="text-white">
                      <span className="font-medium">{s.city}</span>
                      <span className="text-white/55"> — {s.dept}</span>
                    </p>
                    {s.address && (
                      <p className="text-white/60 text-sm mt-0.5" data-testid={`studio-address-${s.city.split(',')[0].toLowerCase().replace(/\s+/g, '-')}`}>
                        {s.address}
                        {s.addressNote && (
                          <span className="text-white/40 italic ml-2">({s.addressNote})</span>
                        )}
                      </p>
                    )}
                    <p className="text-white/65 text-sm">
                      <a href={`tel:${s.phone}`} className="hover:text-white">{s.phone}</a>
                      {s.subtitle && (
                        <>
                          <span className="text-white/30 mx-2">·</span>
                          <span className="text-white/55">{s.subtitle}</span>
                        </>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[10px] tracking-[0.3em] text-white/45 mb-3">PRESS</p>
              <a href={`mailto:${contactInfo.press}`} className="text-white hover:text-[#FF3D2E] transition-colors">
                {contactInfo.press}
              </a>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-7 space-y-10">
          {previewContext && (
            <div
              className="border border-[#FF3D2E]/40 bg-[#FF3D2E]/[0.06] px-5 py-4 flex items-start gap-3 animate-[fadeUp_400ms_ease-out]"
              data-testid="contact-preview-context"
            >
              <Sparkles className="w-4 h-4 text-[#FF3D2E] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-[0.3em] text-[#FF3D2E] mb-1">— YOUR SMG PREVIEW IS ATTACHED</p>
                <p className="text-white/85 text-sm leading-relaxed">
                  We've pre-filled the brief with the thesis we generated for <span className="font-medium">{previewContext.brand}</span>. Edit or replace anything below.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPreviewContext(null);
                  setForm((f) => ({ ...f, company: '', brief: '' }));
                  try { localStorage.removeItem('smg_brand_preview_lead'); } catch {}
                }}
                className="text-white/40 hover:text-white text-[10px] tracking-[0.25em]"
                data-testid="contact-preview-clear"
              >
                CLEAR
              </button>
            </div>
          )}

          <FieldRow label="01 — YOUR NAME">
            <input
              type="text"
              value={form.name}
              onChange={onChange('name')}
              placeholder="Full name"
              className="w-full bg-transparent border-b border-white/15 focus:border-[#FF3D2E] outline-none py-3 text-white text-base placeholder:text-white/30 transition-colors"
            />
          </FieldRow>

          <FieldRow label="02 — EMAIL">
            <input
              type="email"
              value={form.email}
              onChange={onChange('email')}
              placeholder="name@brand.com"
              className="w-full bg-transparent border-b border-white/15 focus:border-[#FF3D2E] outline-none py-3 text-white text-base placeholder:text-white/30 transition-colors"
            />
          </FieldRow>

          <FieldRow label="03 — COMPANY">
            <input
              type="text"
              value={form.company}
              onChange={onChange('company')}
              placeholder="Company / brand"
              className="w-full bg-transparent border-b border-white/15 focus:border-[#FF3D2E] outline-none py-3 text-white text-base placeholder:text-white/30 transition-colors"
            />
          </FieldRow>

          <FieldRow label="04 — BUDGET RANGE">
            <div className="flex flex-wrap gap-2 pt-2">
              {budgetRanges.map((b) => (
                <button
                  type="button"
                  key={b}
                  onClick={() => setForm((f) => ({ ...f, budget: b }))}
                  className={`px-4 py-2.5 min-h-[44px] text-[11px] tracking-[0.2em] border transition-colors ${
                    form.budget === b
                      ? 'border-[#FF3D2E] bg-[#FF3D2E]/10 text-white'
                      : 'border-white/15 text-white/70 hover:border-white/40 hover:text-white'
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
          </FieldRow>

          <FieldRow label="05 — PROJECT BRIEF">
            <textarea
              rows={4}
              value={form.brief}
              onChange={onChange('brief')}
              placeholder="Tell us about the brand, the goal, and the timeline…"
              className="w-full bg-transparent border-b border-white/15 focus:border-[#FF3D2E] outline-none py-3 text-white text-base placeholder:text-white/30 resize-none transition-colors"
            />
          </FieldRow>

          <button
            type="submit"
            disabled={sending}
            className="group inline-flex items-center gap-2 px-8 py-4 bg-[#FF3D2E] text-white text-[11px] tracking-[0.3em] hover:bg-[#ff5446] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? 'SENDING…' : 'SEND TRANSMISSION'}
            <ArrowUpRight className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </form>
      </div>
    </section>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="grid md:grid-cols-12 gap-3 md:gap-6 items-start">
      <p className="md:col-span-3 text-[10px] tracking-[0.3em] text-white/45 pt-3">{label}</p>
      <div className="md:col-span-9">{children}</div>
    </div>
  );
}
